import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Users, Plus, Search, Eye, IndianRupee } from 'lucide-react';
import Layout from '@/components/Layout';

export default function TenantsPage() {
  const [pgs, setPgs] = useState([]);
  const [selectedPg, setSelectedPg] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPgs();
  }, []);

  useEffect(() => {
    if (selectedPg) {
      fetchTenants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPg]);

  const fetchPgs = async () => {
    try {
      const response = await api.getPGs();
      setPgs(response.data);
      if (response.data.length > 0) {
        setSelectedPg(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to fetch PGs');
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await api.getTenants(selectedPg);
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.phone.includes(searchTerm)
  );

  const activeTenants = filteredTenants.filter(t => t.status === 'active');
  const vacatedTenants = filteredTenants.filter(t => t.status === 'vacated');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading">Tenants</h1>
            <p className="text-slate-600 mt-1">Manage your PG tenants</p>
          </div>
          <Button
            onClick={() => navigate('/tenants/add')}
            className="h-12 px-6 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold shadow-sm"
            data-testid="add-tenant-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Tenant
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          {pgs.length > 1 && (
            <select
              value={selectedPg}
              onChange={(e) => setSelectedPg(e.target.value)}
              className="h-11 px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
              data-testid="tenant-pg-selector"
            >
              {pgs.map((pg) => (
                <option key={pg.id} value={pg.id}>{pg.name}</option>
              ))}
            </select>
          )}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-11 pl-10 rounded-lg"
              data-testid="tenant-search-input"
            />
          </div>
        </div>

        {activeTenants.length === 0 && vacatedTenants.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border border-slate-200">
            <Users className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No tenants yet</h3>
            <p className="text-slate-600 mb-4">Add your first tenant to get started</p>
            <Button
              onClick={() => navigate('/tenants/add')}
              className="h-11 px-6 rounded-xl"
              data-testid="empty-add-tenant-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Tenant
            </Button>
          </div>
        ) : (
          <>
            {activeTenants.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-4 font-heading">Active Tenants ({activeTenants.length})</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeTenants.map((tenant) => (
                    <div
                      key={tenant.id}
                      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/tenants/${tenant.id}`)}
                      data-testid={`tenant-card-${tenant.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-primary">{tenant.name.charAt(0)}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{tenant.name}</h3>
                            <p className="text-sm text-slate-500">{tenant.phone}</p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          Active
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Monthly Rent:</span>
                          <span className="font-semibold text-slate-900">₹{tenant.monthlyRent.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Balance:</span>
                          <span className={`font-semibold ${
                            tenant.balance >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            ₹{Math.abs(tenant.balance).toLocaleString('en-IN')}
                            {tenant.balance < 0 ? ' Due' : ' Advance'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Join Date:</span>
                          <span className="font-medium text-slate-900">{new Date(tenant.joinDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      </div>
                      <Button
                        className="w-full mt-4 h-10 rounded-lg"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tenants/${tenant.id}`);
                        }}
                        data-testid={`view-tenant-${tenant.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vacatedTenants.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-4 font-heading">Vacated Tenants ({vacatedTenants.length})</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vacatedTenants.map((tenant) => (
                    <div
                      key={tenant.id}
                      className="bg-slate-50 rounded-xl border border-slate-200 p-5 opacity-75"
                      data-testid={`vacated-tenant-card-${tenant.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-slate-500">{tenant.name.charAt(0)}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-700">{tenant.name}</h3>
                            <p className="text-sm text-slate-500">{tenant.phone}</p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-slate-200 text-slate-700 text-xs font-semibold rounded-full">
                          Vacated
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Vacate Date:</span>
                          <span className="font-medium text-slate-700">{new Date(tenant.vacateDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
